import { t } from './i18n';
import React, { useEffect, useState } from 'react';
import { api } from './api';

const COLORS = {
  primary: '#1a4d8f',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  bg: '#f7f8fa',
  success: '#059669',
  warning: '#d97706',
  danger: '#dc2626',
  accent: '#d4af37',
};

const HOTEL_TYPES = new Set(['kosher_hotel', 'accommodation', 'shabbat_friendly_accommodation', 'kosher_vacation']);
const TOUR_TYPES = new Set(['jewish_tour']);
const DRIVER_TYPES = new Set(['jewish_driver']);

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
};
const btnPrimary = {
  padding: '8px 16px',
  background: COLORS.primary,
  color: 'white',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px',
  background: 'white',
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  const diff = (new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24);
  return diff > 0 ? Math.ceil(diff) : 0;
}

// ────────────────────────────────────────────────────────────────
// Modal for sending a request. Works for both rooms and tour instances.
// Parent passes either `room` or `tourInstance`.
function RequestModal({ open, onClose, establishment, room, tourInstance, driverTrip, customDriver, user, onLogin, onSent }) {
  const today = new Date().toISOString().slice(0, 10);
  const [checkIn, setCheckIn] = useState(tourInstance?.tour_date || today);
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [blockedRanges, setBlockedRanges] = useState([]);
  // Driver-ride fields
  const [name, setName] = useState('');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [pickupAt, setPickupAt] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [passengers, setPassengers] = useState(1);

  const isTour = !!tourInstance;
  const isDriver = !!driverTrip || !!customDriver;

  useEffect(() => {
    if (!open) return;
    // Reset
    setCheckIn(tourInstance?.tour_date || today);
    setCheckOut('');
    setAdults(2);
    setChildren(0);
    setMessage('');
    setError('');
    setName([user?.first_name, user?.last_name].filter(Boolean).join(' '));
    setPickup('');
    setDropoff('');
    setPickupAt('');
    setFlightNumber('');
    setPassengers(1);
    // Load this room's blocked dates so we can warn before submit
    if (room?.id) {
      api.roomBlockedDates(room.id)
        .then((r) => setBlockedRanges(r.blocks || []))
        .catch(() => setBlockedRanges([]));
    } else {
      setBlockedRanges([]);
    }
  }, [open, room?.id, tourInstance?.id, driverTrip?.id, customDriver, today, tourInstance?.tour_date]);

  // Does the chosen range overlap any block? (half-open intervals)
  const overlappingBlock = (!room || !checkIn || !checkOut) ? null
    : blockedRanges.find((b) => !(b.end_date <= checkIn || b.start_date >= checkOut));

  if (!open) return null;

  const pricePerUnit = isTour ? parseFloat(tourInstance.price_per_person)
    : isDriver ? (driverTrip ? parseFloat(driverTrip.price) : 0)
    : parseFloat(room.price_per_night);
  const nights = isTour ? 1 : nightsBetween(checkIn, checkOut);
  const guests = (parseInt(adults, 10) || 0) + (parseInt(children, 10) || 0);
  const estimatedTotal = isTour ? pricePerUnit * guests
    : isDriver ? pricePerUnit // flat per ride (0 for custom = "set by driver")
    : pricePerUnit * nights;
  const tripCurrency = driverTrip?.currency || 'EUR';

  const submit = async () => {
    setError('');
    if (!user) { onLogin?.(); return; }
    if (isDriver) {
      if (!name.trim()) { setError(t('Please enter a name for the booking.')); return; }
      if (customDriver && !dropoff.trim()) { setError(t('Please enter a destination.')); return; }
      if (!pickupAt) { setError(t('Please pick a date and time.')); return; }
      if ((parseInt(passengers, 10) || 0) < 1) { setError(t('At least one passenger is required.')); return; }
    } else {
      if (!isTour) {
        if (!checkIn || !checkOut) { setError(t('Please pick check-in and check-out dates.')); return; }
        if (new Date(checkOut) <= new Date(checkIn)) { setError(t('Check-out must be after check-in.')); return; }
      }
      if ((parseInt(adults, 10) || 0) < 1) { setError(t('At least one adult is required.')); return; }
      if (overlappingBlock) {
        setError(`${t('Those dates are unavailable')} (${overlappingBlock.start_date} → ${overlappingBlock.end_date}${overlappingBlock.reason ? ' · ' + overlappingBlock.reason : ''}). ${t('Please pick different dates.')}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const body = { message: message.trim() || null };
      if (isDriver) {
        if (driverTrip) body.driver_trip_id = driverTrip.id;
        body.name = name.trim() || null;
        body.pickup = pickup.trim() || null;
        body.dropoff = dropoff.trim() || null;
        body.pickup_at = pickupAt || null;
        body.flight_number = flightNumber.trim() || null;
        body.passengers = parseInt(passengers, 10) || 1;
      } else if (isTour) {
        body.adults = parseInt(adults, 10);
        body.children = parseInt(children, 10) || 0;
        body.tour_instance_id = tourInstance.id;
      } else {
        body.adults = parseInt(adults, 10);
        body.children = parseInt(children, 10) || 0;
        body.room_id = room.id;
        body.check_in = checkIn;
        body.check_out = checkOut;
      }
      await api.createBookingRequest(establishment.id, body);
      onSent?.();
      onClose();
    } catch (e) {
      setError(e.message || t('Failed to send request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h2 style={{ marginTop: 0, color: COLORS.primary, fontSize: 20 }}>
          {isDriver
            ? (driverTrip ? `${t('Request:')} ${driverTrip.title}` : t('Request a ride'))
            : `${t('Request')} ${isTour ? t('this tour') : `the ${room.room_type}`}`}
        </h2>
        <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 16 }}>
          {establishment.name}
          {isTour && tourInstance.tour_date ? ` · ${tourInstance.tour_date}` : ''}
          {driverTrip && driverTrip.description ? ` · ${driverTrip.description}` : ''}
        </div>

        {isDriver && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Name (who the ride is for)')}</label>
              <input type="text" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Full name")} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Date & time')}</label>
                <input type="datetime-local" style={inputStyle} value={pickupAt} min={`${today}T00:00`} onChange={(e) => setPickupAt(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Flight number (if airport)')}</label>
                <input type="text" style={inputStyle} value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder={t("e.g. AZ610")} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Pickup (optional)')}</label>
                <input type="text" style={inputStyle} value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder={t("e.g. Fiumicino Airport T3")} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>
                  {t('Destination')} {customDriver ? '' : (t("(optional)"))}
                </label>
                <input type="text" style={inputStyle} value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder={t("e.g. Via Veneto 10, Rome")} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Passengers')}</label>
                <input type="number" min="1" style={inputStyle} value={passengers} onChange={(e) => setPassengers(e.target.value)} />
              </div>
            </div>
          </>
        )}

        {!isTour && !isDriver && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Check-in')}</label>
                <input type="date" style={inputStyle} value={checkIn} min={today} onChange={(e) => setCheckIn(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Check-out')}</label>
                <input type="date" style={inputStyle} value={checkOut} min={checkIn || today} onChange={(e) => setCheckOut(e.target.value)} />
              </div>
            </div>
            {nights > 0 && (
              <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>
                {nights} {t('night')}{nights === 1 ? '' : 's'}
              </div>
            )}
            {overlappingBlock && (
              <div style={{
                background: '#fef2f2', color: COLORS.danger,
                padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12,
              }}>
                ⚠ {t('This room is unavailable')} {overlappingBlock.start_date} → {overlappingBlock.end_date}
                {overlappingBlock.reason && ` · ${overlappingBlock.reason}`}
              </div>
            )}
            {blockedRanges.length > 0 && !overlappingBlock && (
              <details style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>
                <summary style={{ cursor: 'pointer' }}>
                  {blockedRanges.length} {t('unavailable period')}{blockedRanges.length === 1 ? '' : 's'} — {t('view')}
                </summary>
                <ul style={{ marginTop: 6, paddingLeft: 16 }}>
                  {blockedRanges.map((b) => (
                    <li key={b.id}>
                      {b.start_date} → {b.end_date}{b.reason ? ` · ${b.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        {!isDriver && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Adults')}</label>
              <input type="number" min="1" style={inputStyle} value={adults} onChange={(e) => setAdults(e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Children')}</label>
              <input type="number" min="0" style={inputStyle} value={children} onChange={(e) => setChildren(e.target.value)} />
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>
            {t('Message to the')} {isTour ? t('tour guide') : isDriver ? t('driver') : t('host')} {t("(optional)")}
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('e.g. "We keep strictly kosher, do you have a kettle in the room?"')}
            maxLength={1000}
          />
        </div>

        {estimatedTotal > 0 && (
          <div style={{
            background: COLORS.bg, padding: '10px 14px', borderRadius: 8, marginBottom: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          }}>
            <span style={{ fontSize: 13, color: COLORS.muted }}>
              {isDriver ? t('Trip price') : `${t('Estimated total')}${isTour ? ` (${guests} ${t('guest')}${guests === 1 ? '' : 's'})` : ` (${nights} ${t('night')}${nights === 1 ? '' : 's'})`}`}
            </span>
            <span style={{ fontWeight: 700, color: COLORS.primary, fontSize: 18 }}>
              {isDriver ? `${tripCurrency} ` : ''}{estimatedTotal.toFixed(2)}
            </span>
          </div>
        )}
        {isDriver && customDriver && (
          <div style={{ background: COLORS.bg, padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, color: COLORS.muted }}>
            {t('The driver will confirm the price for your custom ride.')}
          </div>
        )}

        <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 14 }}>
          {t('This is a request, not a confirmed booking. The')} {isTour ? t('guide') : isDriver ? t('driver') : t('host')} {t('will get an email and confirm')}{isDriver ? ' ' + t('the trip (they may adjust the price or details)') : ' ' + t('availability and payment details')}.
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: COLORS.danger, padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={btnGhost}>{t('Cancel')}</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? t('Sending…') : (user ? t('Send request') : t('Log in to send'))}
          </button>
        </div>
      </div>
    </div>
  );
}

// Owner/admin-only hint shown on a bookable place that has no inventory yet,
// so the manager knows where to enable booking requests. Visitors never see it.
function ManageInventoryNote({ kind }) {
  return (
    <div style={{
      marginTop: 16, padding: '12px 14px', borderRadius: 10,
      border: `1px dashed ${COLORS.accent}`, background: '#fffdf5',
      fontSize: 13, color: COLORS.text,
    }}>
      <strong>{t('Admin/owner only:')}</strong>{' '}
      No {kind === 'tour' ? t('tour dates') : t('rooms')} {t('set up yet, so no booking panel shows to visitors.')}
      {t('Add')} {kind === 'tour' ? t('tour dates') : t('rooms')} in{' '}
      <a href="/dashboard" style={{ color: COLORS.primary, fontWeight: 600 }}>{t('Dashboard → Inventory')}</a>{' '}
      {t('to enable booking requests.')} {kind === 'tour' ? '' : t('For a 3rd-party hotel, you can instead set a "Book Now" link in the edit form.')}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Public list of rooms with a "Request" button on each. Used for hotels.
function RoomsList({ establishment, user, onLogin, onRequest, canManage }) {
  const [rooms, setRooms] = useState(null);

  useEffect(() => {
    if (!establishment?.id) return;
    api.rooms(establishment.id).then(setRooms).catch(() => setRooms([]));
  }, [establishment?.id]);

  if (rooms === null) return null; // still loading
  if (rooms.length === 0) return canManage ? <ManageInventoryNote kind="hotel" /> : null;

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ margin: '0 0 10px', color: COLORS.primary, fontSize: 16 }}>🛏️ {t('Available rooms')}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rooms.map((r) => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
            background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}`,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 14 }}>
                {r.name ? r.name : r.room_type}
                {r.name && <span style={{ fontWeight: 400, color: COLORS.muted }}> · {r.room_type}</span>}
              </div>
              <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                {t('Sleeps')} {r.capacity} · {parseFloat(r.price_per_night).toFixed(2)} {t('/ night')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => user ? onRequest({ room: r }) : onLogin?.()}
              style={btnPrimary}
            >
              {t('Request')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Public list of upcoming tour dates with Request buttons. Used for tours.
function ToursList({ establishment, user, onLogin, onRequest, canManage }) {
  const [tours, setTours] = useState(null);

  useEffect(() => {
    if (!establishment?.id) return;
    api.tourInstances(establishment.id).then(setTours).catch(() => setTours([]));
  }, [establishment?.id]);

  if (tours === null) return null;
  // Only upcoming dates
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = tours.filter((t) => !t.tour_date || t.tour_date >= today);
  if (upcoming.length === 0) return canManage ? <ManageInventoryNote kind="tour" /> : null;

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ margin: '0 0 10px', color: COLORS.primary, fontSize: 16 }}>📅 {t('Upcoming dates')}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {upcoming.map((t) => {
          const seatsLeft = (t.max_capacity || 0) - (t.current_bookings || 0);
          const sold = seatsLeft <= 0;
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}`,
              flexWrap: 'wrap', opacity: sold ? 0.6 : 1,
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 14 }}>
                  {t.tour_date}
                  {t.start_time && ` · ${t.start_time.slice(0, 5)}`}
                </div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                  {parseFloat(t.price_per_person).toFixed(2)} {t('/ person')}
                  {t.max_capacity ? ` · ${sold ? t('sold out') : `${seatsLeft} ${t('seat')}${seatsLeft === 1 ? '' : 's'} ${t('left')}`}` : ''}
                </div>
              </div>
              <button
                type="button"
                disabled={sold}
                onClick={() => user ? onRequest({ tourInstance: t }) : onLogin?.()}
                style={{ ...btnPrimary, opacity: sold ? 0.5 : 1, cursor: sold ? 'not-allowed' : 'pointer' }}
              >
                {sold ? t('Sold out') : t('Request')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Public driver panel: preset trips (each with a Request button) plus a
// "Request a custom ride" button for anything not covered by a preset.
function DriverPanel({ establishment, user, onLogin, onRequest }) {
  const [trips, setTrips] = useState(null);

  useEffect(() => {
    if (!establishment?.id) return;
    api.driverTrips(establishment.id).then(setTrips).catch(() => setTrips([]));
  }, [establishment?.id]);

  if (trips === null) return null; // still loading

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ margin: '0 0 10px', color: COLORS.primary, fontSize: 16 }}>🚗 {t('Book a ride')}</h3>

      {trips.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {trips.map((t) => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}`, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 14 }}>{t.title}</div>
                {t.description && <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>{t.description}</div>}
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                  {parseFloat(t.price) > 0 ? `${t.currency || "EUR"} ${parseFloat(t.price).toFixed(2)}` : t('Price on request')}
                </div>
              </div>
              <button type="button" onClick={() => user ? onRequest({ driverTrip: t }) : onLogin?.()} style={btnPrimary}>
                {t('Request')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: COLORS.muted }}>
          {trips.length > 0 ? t('Need a different route?') : t('Tell the driver where you need to go.')}
        </span>
        <button type="button" onClick={() => user ? onRequest({ customDriver: true }) : onLogin?.()} style={btnGhost}>
          {t('Request a custom ride')}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Top-level component placed on the detail page. Picks rooms, tours, or
// driver rides based on establishment.type.
export default function BookingRequest({ establishment, user, onLogin }) {
  const [pending, setPending] = useState(null);   // { room } | { tourInstance } | { driverTrip } | { customDriver } | null
  const [justSent, setJustSent] = useState(false);

  if (!establishment) return null;
  const isHotel = HOTEL_TYPES.has(establishment.type);
  const isTour = TOUR_TYPES.has(establishment.type);
  const isDriver = DRIVER_TYPES.has(establishment.type);
  if (!isHotel && !isTour && !isDriver) return null;

  // Admins manage any place; owners manage their own. Drives the empty-state
  // "add inventory" hint (visitors never see it).
  const canManage = !!user && (user.role === 'admin'
    || (user.role === 'owner' && establishment.owner_id === user.id));

  return (
    <>
      {isHotel && (
        <RoomsList
          establishment={establishment}
          user={user}
          onLogin={onLogin}
          onRequest={setPending}
          canManage={canManage}
        />
      )}
      {isTour && (
        <ToursList
          establishment={establishment}
          user={user}
          onLogin={onLogin}
          onRequest={setPending}
          canManage={canManage}
        />
      )}
      {isDriver && (
        <DriverPanel
          establishment={establishment}
          user={user}
          onLogin={onLogin}
          onRequest={setPending}
        />
      )}

      {justSent && (
        <div style={{
          background: '#ecfdf5', border: `1px solid #6ee7b7`, color: '#065f46',
          padding: 12, borderRadius: 8, marginTop: 12, fontSize: 14,
        }}>
          ✓ {t('Your request was sent. The')} {isTour ? t('guide') : isDriver ? t('driver') : t('host')} {t('will reply by email. You can also see it under')} <a href="/profile" style={{ color: '#065f46', fontWeight: 600 }}>{t('My profile')}</a>.
        </div>
      )}

      <RequestModal
        open={!!pending}
        onClose={() => setPending(null)}
        establishment={establishment}
        room={pending?.room}
        tourInstance={pending?.tourInstance}
        driverTrip={pending?.driverTrip}
        customDriver={pending?.customDriver}
        user={user}
        onLogin={onLogin}
        onSent={() => { setJustSent(true); setTimeout(() => setJustSent(false), 12000); }}
      />
    </>
  );
}
